import { useState } from "react";
import { Trash, Smiley, PencilSimple, Check, X as XIcon } from "@phosphor-icons/react";
import { fmt, initials, EMOJIS, API } from "./constants";
import detectMediaUrl from "./detectMediaUrl";

export default function ChatMessage({
  message, currentUser, pickerFor, onOpenPicker,
  onReact, onDelete, onEdit,
  canDelete, canEdit = false,
}) {
  const m = message;
  const mine = m.user_id === currentUser?.id;
  const color = m.user_color || "#005CFF";
  const reactions = m.reactions || {};
  const reactionEntries = Object.entries(reactions).filter(([, arr]) => (arr || []).length > 0);
  const inlineMedia = (!m.attachments || m.attachments.length === 0) ? detectMediaUrl(m.text) : null;

  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(m.title || "");
  const [draftText, setDraftText] = useState(m.text || "");
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraftTitle(m.title || "");
    setDraftText(m.text || "");
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await onEdit(m.id, { title: draftTitle, text: draftText });
      setEditing(false);
    } catch { /* toast handled by parent */ }
    finally { setSaving(false); }
  };

  return (
    <div className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: color + "33", color }}>
        {initials(m.user_name)}
      </div>
      <div className={`max-w-[78%] md:max-w-md group ${mine ? "items-end" : "items-start"} flex flex-col relative`}>
        <div className="flex items-center gap-1.5 mb-0.5 text-[10px] text-white/50">
          <span className="font-semibold" style={{ color }}>{m.user_name}</span>
          <span>·</span>
          <span>{fmt(m.created_at)}</span>
          {m.edited_at && <span className="italic text-white/40">· modifié</span>}
          {canEdit && !editing && (
            <button
              onClick={startEdit}
              className="opacity-0 group-hover:opacity-100 text-white/60 hover:text-[#7CB2FF] ml-1"
              title="Modifier (5 min max)"
              data-testid={`chat-edit-${m.id}`}
            >
              <PencilSimple size={11} />
            </button>
          )}
          {canDelete && (
            <button onClick={() => onDelete(m.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 ml-1" data-testid={`chat-delete-${m.id}`}><Trash size={11} /></button>
          )}
        </div>
        <div className={`relative rounded-2xl px-3 py-2 text-sm ${mine ? "bg-[#005CFF]/25 border border-[#005CFF]/40" : "bg-white/[0.04] border border-white/10"}`}>
          {editing ? (
            <div className="space-y-2 min-w-[240px]" data-testid={`chat-edit-form-${m.id}`}>
              <input
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value.slice(0, 100))}
                placeholder="Titre (optionnel)"
                className="w-full bg-black/40 border border-purple-500/30 rounded-lg px-2 py-1.5 text-xs font-semibold outline-none focus:border-purple-500/60"
                data-testid={`chat-edit-title-${m.id}`}
              />
              <textarea
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                rows={3}
                className="w-full resize-none bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-[#005CFF]/50"
                data-testid={`chat-edit-text-${m.id}`}
              />
              <div className="flex items-center justify-end gap-1">
                <button
                  onClick={() => setEditing(false)}
                  disabled={saving}
                  className="text-[11px] px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 flex items-center gap-1"
                  data-testid={`chat-edit-cancel-${m.id}`}
                >
                  <XIcon size={10} /> Annuler
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="text-[11px] px-2 py-1 rounded-lg bg-[#005CFF] hover:bg-[#0047CC] disabled:opacity-50 flex items-center gap-1 font-semibold"
                  data-testid={`chat-edit-save-${m.id}`}
                >
                  <Check size={10} weight="bold" /> Enregistrer
                </button>
              </div>
            </div>
          ) : (
            <>
              {m.title && (
                <div className="font-display font-bold text-[15px] leading-tight mb-1.5 text-white" data-testid={`chat-msg-title-${m.id}`}>
                  {m.title}
                </div>
              )}
              {inlineMedia ? (
                inlineMedia.kind === "photo" ? (
                  <a href={inlineMedia.url} target="_blank" rel="noreferrer" data-testid={`chat-msg-media-photo-${m.id}`}>
                    <img src={inlineMedia.url} alt="" className="rounded-lg h-auto w-auto max-w-[300px] max-h-64 object-contain cursor-zoom-in" />
                  </a>
                ) : (
                  <video src={inlineMedia.url} controls className="rounded-lg h-auto w-auto max-w-[300px] max-h-64 bg-black" data-testid={`chat-msg-media-video-${m.id}`} />
                )
              ) : (
                m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>
              )}
              {m.attachments && m.attachments.length > 0 && (
                <div className={`mt-${m.text ? "2" : "0"} grid gap-1.5 ${m.attachments.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {m.attachments.map((att, idx) => {
                    const attKey = att.external_url || att.filename || `${att.kind}-${idx}`;
                    if (att.kind === "gif" && att.external_url) {
                      return <img key={attKey} src={att.external_url} alt="gif" className="rounded-lg h-auto w-auto max-w-[300px] max-h-64 object-contain bg-black" loading="lazy" />;
                    }
                    const url = `${API}/chat/attachments/${m.id}/${idx}`;
                    if (att.kind === "video") {
                      const posterUrl = att.poster_file_id ? `${API}/files/${att.poster_file_id}` : undefined;
                      return <video key={attKey} src={url} poster={posterUrl} controls preload="metadata" className="rounded-lg h-auto w-auto max-w-[300px] max-h-64 bg-black" />;
                    }
                    if (att.kind === "photo") {
                      return <a key={attKey} href={url} target="_blank" rel="noreferrer"><img src={url} alt="" className="rounded-lg h-auto w-auto max-w-[300px] max-h-64 object-contain cursor-zoom-in" /></a>;
                    }
                    return <a key={attKey} href={url} target="_blank" rel="noreferrer" className="text-xs text-[#7CB2FF] underline break-all">{att.filename}</a>;
                  })}
                </div>
              )}
            </>
          )}

          {!editing && (
            <button
              data-reaction-trigger
              onClick={(e) => { e.stopPropagation(); onOpenPicker(pickerFor === m.id ? null : m.id); }}
              className={`absolute -bottom-3 ${mine ? "-left-3" : "-right-3"} w-7 h-7 rounded-full bg-neutral-900 border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/30 hover:scale-110 active:scale-95 transition opacity-70 md:opacity-0 md:group-hover:opacity-100`}
              title="Réagir"
              data-testid={`chat-react-btn-${m.id}`}
            >
              <Smiley size={14} />
            </button>
          )}

          {pickerFor === m.id && !editing && (
            <div
              data-reaction-picker
              className={`absolute z-30 ${mine ? "left-0" : "right-0"} -top-14 bg-neutral-900/95 backdrop-blur-md border border-white/15 rounded-full shadow-2xl px-2 py-1.5 flex gap-0.5 animate-in fade-in zoom-in-95 duration-150`}
              onClick={(e) => e.stopPropagation()}
              data-testid={`chat-emoji-picker-${m.id}`}
            >
              {EMOJIS.map((emo) => {
                const active = (reactions[emo] || []).includes(currentUser?.id);
                return (
                  <button
                    key={emo}
                    onClick={() => onReact(m.id, emo)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-lg hover:bg-white/10 hover:scale-125 active:scale-90 transition ${active ? "bg-[#005CFF]/40" : ""}`}
                    data-testid={`chat-emoji-${emo}-${m.id}`}
                  >
                    {emo}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {reactionEntries.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${mine ? "justify-end" : ""}`}>
            {reactionEntries.map(([emo, uids]) => {
              const active = (uids || []).includes(currentUser?.id);
              return (
                <button
                  key={emo}
                  onClick={() => onReact(m.id, emo)}
                  className={`text-[11px] leading-none px-1.5 py-1 rounded-full border flex items-center gap-1 transition active:scale-90 hover:scale-105 ${active ? "bg-[#005CFF]/30 border-[#005CFF]/60 text-white" : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10"}`}
                  data-testid={`chat-reaction-${emo}-${m.id}`}
                  title={active ? "Retirer" : "Réagir"}
                >
                  <span className="text-sm">{emo}</span>
                  <span className="font-semibold tabular-nums">{(uids || []).length}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
